<template>
  <div class="situation-step">
    <b-row>
      <b-col>
        <p v-if="skin.doorstromer">
          In bepaalde situaties kan het zijn dat u op basis van aanvullende kenmerken over uw woon- en/of werksituatie extra voorrang kunt krijgen. Vul daarom onderstaande vragen volledig in.
        </p>
        <p v-else>
          Om beter inzicht te krijgen in uw situatie vragen wij u om hieronder een aantal vragen te beantwoorden.
        </p>
        <br/>
      </b-col>
    </b-row>
    <form-loader v-if="loading"/>
    <template v-if="!loading">

      <div class="extra-questions mb-5" v-if="extraQuestion.length">
        <h2>Uw binding met de regio {{skin.region}}</h2>

        <div class="mb-5" v-for="(question, i) in extraQuestion">
          <div class="row question">
            <div class="col-sm-12">
              <h3 v-html="question.description">
              </h3>
            </div>
          </div>
          <b-row :class="{'is-invalid': (!isAnswerValid(extraQuestion[i].chosenAnswer, question.availableAnswers) && highlightInvalid)}">
            <b-col sm="12">
              <b-form-select
                      name="corp"
                      v-model="extraQuestion[i].chosenAnswer"
                      :options="question.availableAnswers"
              />
            </b-col>
          </b-row>
        </div>
      </div>

      <h2 v-if="skin.doorstromer">Komt u in aanmerking voor Doorstromer?</h2>
      <h2 v-else>Vragen over uw woonsituatie</h2>

      <sub-header class="question">Woont u in een koopwoning?</sub-header>
      <b-row>
        <b-col sm="12">
          <b-form-radio-group
                  v-model="situation.doorstromer.answers.buy"
                  :options="formOptions.buy"
                  name="buy"
                  :class="{'is-invalid radio-row': (!buyValid && highlightInvalid)}"
          />
        </b-col>
      </b-row>

      <template v-if="!situation.doorstromer.answers.buy">
        <sub-header class="question">
          Woont u in een huurwoning in {{ situation.doorstromer.texts.region }}?
          <span @click="openModal('municipalities-modal')" class="pointer">
            <i class="fa fax fa-info-circle"></i>
          </span>
        </sub-header>
        <b-row>
          <b-col sm="12">
            <b-form-radio-group
                    v-model="situation.doorstromer.answers.rental"
                    :options="formOptions.rental"
                    name="rental"
                    :class="{'is-invalid radio-row': (!rentalValid && highlightInvalid)}"
            />
          </b-col>
        </b-row>
      </template>


      <template v-if="situation.doorstromer.answers.rental">
        <sub-header class="question">
          Is uw woning een sociale huurwoning?
          <span @click="openModal('social-house-modal')" class="pointer">
            <i class="fa fax fa-info-circle"></i>
          </span>
        </sub-header>
        <b-row>
          <b-col sm="12">
            <b-form-radio-group
                    v-model="situation.doorstromer.answers.social"
                    :options="formOptions.social"
                    name="social"
                    :class="{'is-invalid radio-row': (!socialValid && highlightInvalid)}"
            />
          </b-col>
        </b-row>
      </template>

      <template v-if="situation.doorstromer.answers.rental">
        <sub-header class="question">Bij wie huurt u?</sub-header>
        <b-row :class="{'is-invalid': (!corpValid && highlightInvalid)}">
          <b-col sm="12">
            <b-form-select
                    name="corp"
                    v-model="situation.doorstromer.answers.corp"
                    :options="situation.doorstromer.landlords"
            />
          </b-col>
        </b-row>
      </template>

      <sub-header class="question">Laat u uw huidige woning leeg achter als u een andere woning gaat huren?</sub-header>
      <b-row>
        <b-col sm="12">
          <b-form-radio-group
                  v-model="situation.doorstromer.answers.renter"
                  :options="formOptions.renter"
                  name="renter"
                  stacked
                  :class="{'is-invalid radio-row': (!renterValid && highlightInvalid)}"
          />
        </b-col>
      </b-row>

      <br>
      <summary-message-box
              title="U bent doorstromer!"
              icon="fa fax fa-check-square"
              :collapsable="true"
              v-if="skin.doorstromer && userIsDoorstromer"
      >
        U krijgt bij advertenties met het label Doorstromers voorrang ten opzichte van andere woningzoekenden.
      </summary-message-box>

      <summary-message-box
              title="U bent geen doorstromer"
              icon="fad fax fa-info-circle"
              iconColor="#666666"
              :collapsable="true"
              v-if="skin.doorstromer && !userIsDoorstromer && situation.doorstromer.answers.renter !== null"
      >
        Uit de gegevens die u heeft ingevuld bij uw inschrijving blijkt dat u geen doorstromer bent.
      </summary-message-box>

      <error-box v-if="postErrors.length > 0" :errors="postErrors"/>

      <FormButtons @next="next" @previous="$emit('previous')"/>

      <b-modal id="social-house-modal" title="Wat is een sociale huurwoning?" hide-footer>
        <p>
          <b>Wat is een sociale huurwoning?</b>
          <br>
          Is de netto huur (= huur zonder servicekosten) van uw huidige woning lager dan €{{skin.socialcutoff}} (in {{skin.year}}) dan is uw
          woning een sociale huurwoning.
          <br><br>
          Is de netto huur (= huur zonder servicekosten) van uw woning hoger, dan is uw woning in sommige gevallen ook
          een sociale huurwoning. Dat is het geval indien op het moment dat u de woning ging huren, de huurprijs een
          sociale huurprijs was. Als u twijfelt of dat het geval is, belt u dan met uw verhuurder.</p>
      </b-modal>

      <b-modal id="municipalities-modal" :title="`Gemeenten van ${situation.doorstromer.texts.region}`" hide-footer>
        <p>
          <b>Welke gemeenten behoren tot {{situation.doorstromer.texts.region}}?</b>
          <br>
          Dit betreft de gemeenten {{situation.doorstromer.texts.municipalities}}
        </p>
      </b-modal>
    </template>
  </div>
</template>

<script>
import FormButtons from '@/components/_registration/FormButtons'
import FormLoader from '@/components/_registration/FormLoader'
import { registrationService } from '@/_services/registration.service'
import SubHeader from '@/components/_registration/SubHeader'
import ErrorBox from '@/components/_registration/ErrorBox'
import SummaryMessageBox from '@/components/_registration/SummaryMessageBox'

export default {
  components: { FormButtons, FormLoader, SubHeader, ErrorBox, SummaryMessageBox },
  data: () => {
    return {
      postErrors: [],
      extraQuestion: [],
      skin: {
        region: process.env.VUE_APP_REGION,
        doorstromer: process.env.VUE_APP_DOORSTROMER === 'true',
        socialcutoff:  '932,93',
        year: new Date().getFullYear()
      },
      situation: {
        doorstromer: {
          texts: {
            municipalities: '',
            region: ''
          },
          landlords: [],
          answers: {
            buy: null,
            corp: '',
            rental: null,
            renter: null,
            social: null,
          }
        }
      },
      formOptions: {
        buy: [
          { text: 'Ja', value: true },
          { text: 'Nee', value: false },
        ],
        rental: [
          { text: 'Ja', value: true },
          { text: 'Nee', value: false },
        ],
        social: [
          { text: 'Ja', value: true },
          { text: 'Nee', value: false },
        ],
        renter: [
          { text: 'Ja. Als ik verhuis zeg ik de huur op van, of verkoop ik mijn huidige woning.', value: true },
          { text: 'Nee. Een van de huidige bewoners blijft in dit huis wonen.', value: false },
        ],
      },
      highlightInvalid: false,
      loading: false,
    }
  },
  name: 'Situation',

  computed: {
    buy () {
      return this.situation.doorstromer.answers.buy
    },
    rental () {
      return this.situation.doorstromer.answers.rental
    },

    buyValid () {
      return this.situation.doorstromer.answers.buy !== null
    },
    renterValid () {
      return this.situation.doorstromer.answers.renter !== null
    },
    rentalValid () {
      return this.situation.doorstromer.answers.buy !== false || this.situation.doorstromer.answers.rental !== null
    },
    socialValid () {
      return this.situation.doorstromer.answers.rental !== true || this.situation.doorstromer.answers.social !== null
    },
    corpValid () {
      return this.situation.doorstromer.answers.rental !== true || this.situation.doorstromer.answers.corp !== ''
    },
    extraQuestionsAreValid () {
      let valid = true
      this.extraQuestion.forEach(q => {
        if (!this.isAnswerValid(q.chosenAnswer, q.availableAnswers)) {
          valid = false
        }
      })
      return valid
    },

    validated () {
      return this.buyValid && this.renterValid && this.rentalValid && this.socialValid && this.corpValid && this.extraQuestionsAreValid
    },

    // get the text value of the landlord that is currently chosen
    chosenLandlordText () {
      // check if landlords is/(can be) chosen
      if (
              this.situation.doorstromer.answers.corp === '' ||
              this.situation.doorstromer.answers.corp === null ||
              this.situation.doorstromer.landlords === [] ||
              this.situation.doorstromer.landlords.length < 1
      ) {
        return null
      }
      let text = null

      this.situation.doorstromer.landlords.forEach((item) => {
        if (item.value === this.situation.doorstromer.answers.corp) {
          text = item.text
        }
      })

      return text
    },

    userIsDoorstromer () {
      return this.situation.doorstromer.answers.renter &&
              !this.situation.doorstromer.answers.buy &&
              this.situation.doorstromer.answers.rental &&
              this.situation.doorstromer.answers.social &&
              this.chosenLandlordText !== 'Anders' &&
              this.chosenLandlordText !== null &&
              this.chosenLandlordText !== ''
    },
  },
  watch: {
    buy () {
      if (this.buy) {
        this.situation.doorstromer.answers.rental = null
      }
    },
    rental () {
      if (!this.rental) {
        this.situation.doorstromer.answers.social = null
        this.situation.doorstromer.answers.corp = ''
      }
    },
  },
  mounted() {
    if(this.$store.state.registration.errors[this.$options.name] !== undefined) {
      this.highlightInvalid = true
    }
  },
  methods: {
    fetchSituation () {
      this.loading = true

      registrationService.getSituation().then(result => {
        this.situation = result.situation
        this.extraQuestion = result.extraQuestion
      }).finally(() => {
        this.loading = false
      })
    },

    isAnswerValid (answer, possibleAnswers) {
      let valid = false
      possibleAnswers.forEach(possibleAnswer => {
        if (answer === possibleAnswer.value) {
          valid = true
        }
      })
      return valid
    },

    submit () {
      let extraQuestionData = []
      this.extraQuestion.forEach(q => {
        extraQuestionData.push({sysname: q.sysname, answer: q.chosenAnswer})
      })

      // replace null by false
      let data = {
        rental: !!this.situation.doorstromer.answers.rental,
        renter: this.situation.doorstromer.answers.renter,
        buy: this.situation.doorstromer.answers.buy,
        social: !!this.situation.doorstromer.answers.social,
        corp: (this.situation.doorstromer.answers.corp === '') ? null : this.situation.doorstromer.answers.corp,
        extraQuestions: extraQuestionData
      }
      registrationService.postSituation(data).then(result => {
        if ('errors' in result) {
          this.postErrors = result.errors
        } else {
          this.postErrors.errors = []
          this.$emit('next')
        }
      })
    },

    next () {
      this.highlightInvalid = true
      if (this.validated) {
        this.$emit('validated', true)
        this.submit()
      }
    },

    openModal(id) {
      this.$bvModal.show(id);
    }
  },

  activated () {
    this.fetchSituation(false)
  },
}
</script>
<style lang="scss" scoped>
.question {
  margin-top: 10px;
}

.is-invalid.radio-row:after {
  content: 'dit veld is verplicht';
  color: red;
  font-size: 11px;
}
</style>


